import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from 'lib/api';
import { consumerApi } from 'lib/consumer-api';
import { API_ENDPOINTS } from 'constants/endpoints';

type ApiClient = typeof api | typeof consumerApi;

export function useMonitoringPortalContext() {
  const { pathname } = useLocation();
  const isConsumer = pathname.startsWith('/consumer');

  return useMemo(() => {
    const apiClient: ApiClient = isConsumer ? consumerApi : api;

    return {
      isConsumer,
      apiClient,
      backToBattlesPath: isConsumer ? '/consumer/battles' : '/battles',
      backToPlayersPath: isConsumer ? '/consumer/users' : '/players',
      playerDetailPath: (id: number | string) =>
        isConsumer ? `/consumer/users/${id}` : `/players/${id}/enforcement`,
      battleDetailPath: (id: number | string) =>
        isConsumer ? `/consumer/battles/${id}` : `/battles/${id}`,
      battlesListWithUserFilter: (userId: number, username: string) =>
        isConsumer
          ? `/consumer/battles?userId=${userId}&username=${encodeURIComponent(username)}`
          : `/battles?userId=${userId}&username=${encodeURIComponent(username)}`,
      battlesListWithPrivateTableFilter: (
        privateTableId: number | string,
        roomCode?: string
      ) => {
        const params = new URLSearchParams({
          privateTableId: String(privateTableId),
          type: '2',
        });
        if (roomCode) params.set('roomCode', roomCode);
        return isConsumer
          ? `/consumer/battles?${params.toString()}`
          : `/battles?${params.toString()}`;
      },
      privateTableDetailPath: (id: number | string) =>
        isConsumer ? `/consumer/private-tables/${id}` : `/private-tables/${id}`,
      transactionsListWithUserFilter: (userId: number, username: string) =>
        isConsumer
          ? `/consumer/transactions?userId=${userId}&username=${encodeURIComponent(username)}`
          : `/wallet?userId=${userId}&username=${encodeURIComponent(username)}`,
      endpoints: {
        playerDetails: (id: string | number) =>
          isConsumer
            ? API_ENDPOINTS.CONSUMER_PORTAL.GET_USER_DETAILS(id)
            : API_ENDPOINTS.ADMIN.PLAYERS.GET_DETAILS(id),
        battleDetails: (id: string | number) =>
          isConsumer
            ? API_ENDPOINTS.CONSUMER_PORTAL.GET_BATTLE_DETAILS(id)
            : API_ENDPOINTS.ADMIN.BATTLES.GET_DETAILS(id),
        roundDetails: (battleId: string | number, roundId: string | number) =>
          isConsumer
            ? API_ENDPOINTS.CONSUMER_PORTAL.GET_BATTLE_ROUND(battleId, roundId)
            : API_ENDPOINTS.ADMIN.BATTLES.GET_ROUND(battleId, roundId),
      },
    };
  }, [isConsumer]);
}
